import React, { useState, useEffect } from 'react';
import {
  Check,
  CheckCircle,
  AlertCircle as _AlertCircle,
  DollarSign,
  Award,
  ChevronRight,
  Mountain as _Mountain,
  Fish,
  Snowflake,
  Footprints,
  Waves,
  Zap
} from 'lucide-react';
import { apiRequest } from '../../../services/api';
import { validateStage2 } from '../../../utils/guideValidation';

const activities = [
  { value: 'rock-climbing', label: 'Rock Climbing & Mountaineering', Icon: _Mountain },
  { value: 'fly-fishing', label: 'Fly Fishing & Angling', Icon: Fish },
  { value: 'skiing', label: 'Skiing & Snowboarding', Icon: Snowflake },
  { value: 'hiking', label: 'Hiking & Backpacking', Icon: Footprints },
  { value: 'kayaking', label: 'Kayaking & Rafting', Icon: Waves },
  { value: 'multi-sport', label: 'Multi-Sport Adventure', Icon: Zap },
];

const certifications = [
  'AMGA Mountain Guide',
  'AMGA Rock Guide',
  'IFMGA Mountain Guide',
  'Wilderness First Responder',
  'Wilderness EMT',
  'Avalanche Level 1',
  'Avalanche Level 2',
  'Avalanche Level 3',
  'Leave No Trace Master',
  'Professional Climbing Instructor',
];

const Step3ServiceInfo = ({ formData = {}, onUpdate, onNext, _onBack, className = '' }) => {
  const [localData, setLocalData] = useState({
    primaryActivity: formData.primaryActivity || '',
    serviceDescription: formData.serviceDescription || '',
    experienceRange: formData.experienceRange || '',
    dayRateCents: formData.dayRateCents || null,
    availabilityEstimate: formData.availabilityEstimate || '',
    certifications: formData.certifications || [],
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSavedProgress();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSavedProgress = async () => {
    try {
      const result = await apiRequest('/api/v1/guides/signup/progress', { method: 'GET' });
      const saved = result.data || {};
      setLocalData(prev => ({
        ...prev,
        primaryActivity: saved.primaryActivity || prev.primaryActivity,
        serviceDescription: saved.serviceDescription || prev.serviceDescription,
        experienceRange: saved.experienceRange || prev.experienceRange,
        dayRateCents: saved.dayRateCents || prev.dayRateCents,
        availabilityEstimate: saved.availabilityEstimate || prev.availabilityEstimate,
        certifications: (saved.certifications?.length > 0) ? saved.certifications : prev.certifications,
      }));
    } catch {
      // Non-fatal — user will fill in the form fresh
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setLocalData(prev => ({ ...prev, [field]: value }));
    if (onUpdate) { onUpdate({ [field]: value }); }
    if (errors[field]) {
      setErrors(prev => { const e = { ...prev }; delete e[field]; return e; });
    }
  };

  const handleDayRateChange = (value) => {
    const cents = Math.round(parseFloat(value || 0) * 100);
    handleChange('dayRateCents', cents);
  };

  const handleCertificationToggle = (cert) => {
    const current = localData.certifications;
    handleChange(
      'certifications',
      current.includes(cert) ? current.filter(c => c !== cert) : [...current, cert]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fakeFormData = {
      primaryActivity: localData.primaryActivity,
      serviceDescription: localData.serviceDescription,
      experienceRange: localData.experienceRange,
      dayRateCents: localData.dayRateCents,
      availabilityEstimate: localData.availabilityEstimate,
    };
    const validation = validateStage2(fakeFormData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      await apiRequest('/api/v1/guides/signup/progress', {
        method: 'PATCH',
        body: JSON.stringify({
          activities: localData.primaryActivity,
          bio: localData.serviceDescription,
          experience: localData.experienceRange,
          pricing: localData.dayRateCents,
          availabilityEstimate: localData.availabilityEstimate,
          certifications: localData.certifications,
          signupStage: 3,
        }),
      });
      if (onNext) { onNext(); }
    } catch (err) {
      setSaveError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const dayRateDisplay = localData.dayRateCents ? (localData.dayRateCents / 100).toFixed(0) : '';
  const selectedActivity = activities.find(a => a.value === localData.primaryActivity);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-gray-500">Loading…</p>
      </div>
    );
  }

  return (
    <div className={`max-w-2xl mx-auto px-4 py-8 ${className}`}>
      {/* Progress Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
          <span>Step 3 of 5</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div className="bg-emerald-600 h-2 rounded-full transition-all duration-300" style={{ width: '60%' }} />
        </div>
      </div>

      {/* Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Tell Us About Your Services</h2>
        <p className="text-gray-600">
          This helps customers find you and understand what you offer. Takes about 8-10 minutes.
        </p>
      </div>

      {saveError && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700 flex items-center">
            <_AlertCircle className="w-4 h-4 mr-2" />
            {saveError}
          </p>
        </div>
      )}

      <form className="space-y-6" onSubmit={handleSubmit}>
        {/* Primary Activity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Primary Guiding Activity
          </label>
          <div className="grid md:grid-cols-2 gap-3">
            {activities.map((activity) => {
              const isSelected = localData.primaryActivity === activity.value;
              return (
                <button
                  key={activity.value}
                  type="button"
                  onClick={() => handleChange('primaryActivity', activity.value)}
                  className={`flex items-center p-4 border-2 rounded-lg transition-all text-left ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50 shadow-sm'
                      : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                  }`}
                >
                  <activity.Icon className={`w-6 h-6 mr-3 ${isSelected ? 'text-emerald-600' : 'text-gray-400'}`} />
                  <span className="text-sm font-medium text-gray-900">{activity.label}</span>
                  {isSelected && <Check className="ml-auto w-5 h-5 text-emerald-600" />}
                </button>
              );
            })}
          </div>
          {localData.primaryActivity && (
            <p className="mt-2 text-xs text-emerald-600 flex items-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Activity selected! This helps customers find you
            </p>
          )}
          {errors.primaryActivity && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.primaryActivity}
            </p>
          )}
        </div>

        {/* Service Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Service Description
          </label>
          <textarea
            value={localData.serviceDescription}
            onChange={(e) => handleChange('serviceDescription', e.target.value)}
            placeholder="e.g., Experienced alpine guide specializing in technical climbing and summit expeditions throughout the Rocky Mountains."
            rows={4}
            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
              errors.serviceDescription ? 'border-red-500' :
              localData.serviceDescription.length >= 50 ? 'border-emerald-300 bg-emerald-50/30' :
              'border-gray-300'
            }`}
          />
          <div className="flex items-center justify-between mt-1.5">
            <p className={`text-xs transition-colors ${
              localData.serviceDescription.length >= 50 ? 'text-emerald-600 font-medium' : 'text-gray-500'
            }`}>
              {localData.serviceDescription.length} / 250 characters
              {localData.serviceDescription.length >= 50 && <span className="ml-1">✓</span>}
            </p>
            {localData.serviceDescription.length > 0 && localData.serviceDescription.length < 50 && (
              <p className="text-xs text-amber-600 flex items-center">
                <_AlertCircle className="w-3 h-3 mr-1" />
                {50 - localData.serviceDescription.length} more for minimum
              </p>
            )}
          </div>
          {errors.serviceDescription && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.serviceDescription}
            </p>
          )}
        </div>

        {/* Years Experience */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Years of Guiding Experience
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { value: '0-2', label: '0-2 years' },
              { value: '3-5', label: '3-5 years' },
              { value: '6-10', label: '6-10 years' },
              { value: '10+', label: '10+ years' }
            ].map((option) => {
              const isSelected = localData.experienceRange === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('experienceRange', option.value)}
                  className={`px-4 py-3 rounded-lg border-2 font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-800'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-emerald-400'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          {localData.experienceRange && (
            <p className="mt-1.5 text-xs text-emerald-600 flex items-center">
              <CheckCircle className="w-3 h-3 mr-1" />
              Great! This helps customers trust your expertise
            </p>
          )}
          {errors.experienceRange && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.experienceRange}
            </p>
          )}
        </div>

        {/* Day Rate */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Standard Day Rate
          </label>
          <div className="relative">
            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="number"
              value={dayRateDisplay}
              onChange={(e) => handleDayRateChange(e.target.value)}
              placeholder="550"
              min="100"
              max="2000"
              className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-colors ${
                errors.dayRate ? 'border-red-500' :
                dayRateDisplay && parseFloat(dayRateDisplay) >= 450 && parseFloat(dayRateDisplay) <= 650 ? 'border-emerald-300 bg-emerald-50/30' :
                'border-gray-300'
              }`}
            />
          </div>
          {errors.dayRate && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.dayRate}
            </p>
          )}
          {!errors.dayRate && (
            <p className="mt-1.5 text-xs text-gray-500">Average day rates in your area: $450-$650</p>
          )}
        </div>

        {/* Availability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Typical Availability
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { value: '1-2', label: 'Weekends Only' },
              { value: '3-4', label: 'Part-Time' },
              { value: 'full-time', label: 'Full-Time' }
            ].map((option) => {
              const isSelected = localData.availabilityEstimate === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleChange('availabilityEstimate', option.value)}
                  className={`p-4 rounded-lg border-2 transition-all text-center focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
                    isSelected
                      ? 'border-emerald-600 bg-emerald-50'
                      : 'border-gray-300 bg-white hover:border-emerald-400'
                  }`}
                >
                  <div className={`text-sm font-medium ${isSelected ? 'text-emerald-800' : 'text-gray-700'}`}>
                    {option.label}
                  </div>
                </button>
              );
            })}
          </div>
          {errors.availabilityEstimate && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.availabilityEstimate}
            </p>
          )}
          <p className="mt-1.5 text-xs text-gray-500 text-center">
            You&apos;ll set your exact calendar later—this is just an estimate
          </p>
        </div>

        {/* Certifications */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Certifications <span className="text-gray-500 font-normal">(Select all that apply)</span>
          </label>
          <div className="grid md:grid-cols-2 gap-2 max-h-60 overflow-y-auto p-2 border border-gray-200 rounded-lg">
            {certifications.map((cert) => (
              <label key={cert} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={localData.certifications.includes(cert)}
                  onChange={() => handleCertificationToggle(cert)}
                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                />
                <span className="ml-2 text-sm text-gray-700">{cert}</span>
              </label>
            ))}
          </div>
          {localData.certifications.length > 0 && (
            <div className="mt-2">
              {localData.certifications.length >= 3 ? (
                <p className="text-sm text-emerald-600 flex items-center">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Excellent! {localData.certifications.length} certifications will significantly boost your bookings
                </p>
              ) : (
                <p className="text-xs text-blue-600 flex items-center">
                  <Check className="w-3 h-3 mr-1" />
                  {localData.certifications.length} certification{localData.certifications.length !== 1 ? 's' : ''} selected
                  {localData.certifications.length < 3 && ' • Consider adding more to stand out'}
                </p>
              )}
            </div>
          )}
          {errors.certifications && (
            <p className="mt-1.5 text-sm text-red-600 flex items-center">
              <_AlertCircle className="w-4 h-4 mr-1" />
              {errors.certifications}
            </p>
          )}
          <p className="mt-2 text-xs text-gray-500">
            We&apos;ll verify these in the next step. Don&apos;t have certifications yet?
            <a href="#" className="text-emerald-600 hover:text-emerald-700 underline ml-1">
              Learn about certification options
            </a>
          </p>
        </div>

        {/* Preview Profile Card */}
        {localData.primaryActivity && localData.serviceDescription && localData.dayRateCents && (
          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-lg p-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">
              Preview: How your profile will appear to customers
            </h4>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-gray-200 rounded-full mr-4"></div>
                <div className="flex-1">
                  <h5 className="font-semibold text-gray-900">
                    {formData.firstName} {formData.lastName}
                  </h5>
                  <p className="text-xs text-gray-600 mb-2">
                    {selectedActivity?.label} • {formData.location}
                  </p>
                  <p className="text-sm text-gray-700 line-clamp-2">{localData.serviceDescription}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-lg font-bold text-gray-900">
                      ${dayRateDisplay}
                      <span className="text-sm font-normal text-gray-600"> per day</span>
                    </span>
                    <div className="flex items-center text-xs text-gray-600">
                      <Award className="w-4 h-4 text-emerald-600 mr-1" />
                      {localData.certifications.length} certs
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-6 py-3 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
          >
            {saving ? 'Saving…' : 'Save & Continue'}
            {!saving && <ChevronRight className="ml-2 w-4 h-4" />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default Step3ServiceInfo;
